// Tier 2 coaches migration, Phase B: the real intended replacement for the coaches portion of
// App.jsx's onSnapshot(doc(db,"warriors","coaches")) listener — currently:
//   unsubs.push(onSnapshot(doc(db,"warriors","coaches"),snap=>{
//     const raw=snap.exists()?(snap.data().coaches||[]):[];
//     const seen=new Set();
//     const list=raw.map(x=>(!x.id||seen.has(x.id))?{...x,id:uid()}:(seen.add(x.id),x));
//     setCoaches(list); ...
//   }));
// which subscribes to the single warriors/coaches document and reads its `coaches` array field.
// This module instead subscribes to the new top-level `coaches` collection (one document per
// coach, doc ID = the coach's existing id — see scripts/migrate-coaches.cjs) and reconstructs an
// equivalent in-memory array.
//
// Wired into App.jsx since Phase C+D (watchCoachesCollection/watchCoachesCollectionForNodes are
// both imported and actively used in the admin data-loading effect) — the header comment above
// predates that cutover and was never updated; kept as historical context, not a current statement
// of whether it's in use.
//
// Dedup-on-load (the `seen`/uid() fallback above) is intentionally NOT reproduced here: it existed
// to paper over duplicate/missing `id` values inside a single hand-edited array field. In the new
// collection, `id` IS the Firestore document ID, so duplicates are structurally impossible and a
// missing id can't happen (every doc has an ID) — same reasoning already applied to
// playersCollection.js and teamsCollection.js.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one coaches/{id} document into the shape the app's in-memory `coaches` array has
// always used. `nodeId` USED to be dropped here too ("nothing client-side reads it yet") — that
// stopped being true once the shared node-filter component (Roster View, Participation Tracker,
// Stats, Roster Builder pool) needed nodeId on the collections it filters. Coaches aren't filtered
// by that component today, but the same silent-failure risk applies the moment something does read
// coach.nodeId, so this is fixed proactively rather than case-by-case (mirrors playersDocToRecord/
// teamsDocToRecord exactly). Checked the one write site that touches a coach object (the coach
// editor's SAVE/REMOVE handlers, src/App.jsx) — both build their write payload from explicitly
// named fields, never a whole-object spread, so carrying nodeId through here doesn't leak it into
// any write that shouldn't have it.
// Coach `id` is already a string (format "c_<suffix>" — assigned via `c_${uid()}` in the coach
// editor's SAVE handler in src/App.jsx), same as teams and unlike players, so there's no numeric
// conversion to undo here — the doc ID and the `id` field already match as-is.
export function coachesDocToRecord(docId, data) {
  return { ...data, id: docId };
}

// Live-subscription replacement for onSnapshot(doc(db,"warriors","coaches"), ...) — same
// ergonomics (takes the setter, returns an unsubscribe function) so Phase C+D can drop this in
// place of the existing listener with minimal disruption to the surrounding effect.
export function watchCoachesCollection(setter) {
  return onSnapshot(collection(db, 'coaches'), (snap) => {
    setter(snap.docs.map((d) => coachesDocToRecord(d.id, d.data())));
  });
}

// Node-scoped variant (third pilot, mirroring watchTeamsCollectionForNodes/
// watchPlayersCollectionForNodes exactly) — constrained via where('nodeId','in',nodeIds) to a
// caller-supplied permitted-node list. Used for the admin role only; coach/parent keep the unscoped
// watchCoachesCollection above, matching the coaches/{coachId} rule's unscoped isCoach()/isParent()
// read branches. Mirrors the rule's admin branch exactly — the rule is what actually enforces this,
// this is just what makes the query itself succeed instead of being rejected as unprovable. Same
// 30-value 'in' operator ceiling applies as documented in watchPlayersCollectionForNodes.
export function watchCoachesCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'coaches'), where('nodeId', 'in', nodeIds)), (snap) => {
    setter(snap.docs.map((d) => coachesDocToRecord(d.id, d.data())));
  });
}
